/**
 * Latitude/longitude pair plus a click-to-place map.
 *
 * The same control is inlined in `pages/stations/stationAdd.tsx` and
 * `stationEdit.tsx`; this is the extracted version rather than a third copy.
 * It follows the edit page, which places a declarative `<Marker>` instead of
 * mutating the Leaflet map with `window.L.marker(...).addTo(map)`.
 *
 * react-leaflet here is v2: `onClick` lives on `<Map>` and there are no
 * `useMap`/`useMapEvents` hooks.
 */

import React, { useCallback, useRef } from 'react';
import Stack from '@mui/material/Stack';
//@ts-ignore
import { Map, Marker } from 'react-leaflet/es';

import { GenericMapTileLayer } from '../mapComponent';
import { MovaticFieldGroupOutlinedInput } from '../Redesing/movatic-field-group';

export interface MerchantStoreLocationPickerProps {
  lat: string;
  lng: string;
  onChange: (next: { lat: string; lng: string }) => void;
  latError?: string;
  lngError?: string;
  disabled?: boolean;
  /** Leaflet icon for the marker. Falls back to Leaflet's default pin. */
  icon?: any;
}

/** Leaflet hands back full precision; the column is a float8 either way. */
const round = (value: number) => parseFloat(value.toFixed(10)).toString(10);

const MerchantStoreLocationPicker: React.FC<MerchantStoreLocationPickerProps> = ({
  lat,
  lng,
  onChange,
  latError,
  lngError,
  disabled,
  icon,
}) => {
  const mapRef = useRef<any>(null);

  const handleMapClick = useCallback(
    (e: { latlng: { lat: number; lng: number } }) => {
      if (disabled) return;
      onChange({ lat: round(e.latlng.lat), lng: round(e.latlng.lng) });
    },
    [disabled, onChange]
  );

  const numericLat = parseFloat(lat);
  const numericLng = parseFloat(lng);
  const hasMarker =
    !Number.isNaN(numericLat) &&
    !Number.isNaN(numericLng) &&
    Math.abs(numericLat) <= 90 &&
    Math.abs(numericLng) <= 180;

  return (
    <>
      <MovaticFieldGroupOutlinedInput
        id="lat"
        type="number"
        label="Latitude"
        description="Where the marker sits in the rider app. Enter a value or click the map."
        value={lat}
        allowNegativeNumbers
        hasError={Boolean(latError)}
        errorMessage={latError}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          onChange({ lat: e.target.value, lng })
        }
      />

      <MovaticFieldGroupOutlinedInput
        id="lng"
        type="number"
        label="Longitude"
        description="Enter a value or click the map."
        value={lng}
        allowNegativeNumbers
        hasError={Boolean(lngError)}
        errorMessage={lngError}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          onChange({ lat, lng: e.target.value })
        }
      />

      <Stack pt={2}>
        <Map
          ref={mapRef}
          center={hasMarker ? [numericLat, numericLng] : [0, 0]}
          zoom={hasMarker ? 15 : 2}
          maxBounds={[
            [90, 180],
            [-90, -180],
          ]}
          onClick={handleMapClick}
          style={{ height: '30em' }}
        >
          {hasMarker && <Marker position={[numericLat, numericLng]} icon={icon} />}
          <GenericMapTileLayer />
        </Map>
      </Stack>
    </>
  );
};

export default MerchantStoreLocationPicker;
